/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GridOptions, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  GridOptionsExt,
  BaseService,
  ISortModel,
  ProxAgFns,
  QueryComponent,
  Roles,
  StatusChangeDialog,
  StatusService,
  RowSelectionSingle,
} from '@core';
import { Account, AccountAdmin, ActiveStatus, ActiveStatusEnum, JobOrder, JobOrderStatusEnum, ProximityRightEnum } from '@models';
import { AccountDbQueryService } from './../services/account-db-query.service';
import { UtilFns } from '@utils';
import * as _ from 'lodash';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountActivationService } from '../services/account-activation.service';

@Component({
  selector: 'prox-account-administrator-list',
  templateUrl: './account-administrator-list.component.html',
  standalone: false,
})
export class AccountAdminListComponent extends QueryComponent {
  accountId?: string;
  account!: Account;
  administrators: AccountAdmin[] = [];
  visibleAdministrators: AccountAdmin[] = [];
  activeStatuses: ActiveStatus[] = [];
  editLabel = 'Edit';
  editLabelWidth = 50;
  gridOptions: GridOptionsExt = {};
  joGridOptions: GridOptionsExt = {};
  jobOrders: JobOrder[] = [];
  includeHierarchy = false;
  showInactive = false;

  isValidateActivated = false;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    private statusService: StatusService,
    public pngDialogService: DialogService,
    public activationService: AccountActivationService,
  ) {
    super(baseService, route, dbQueryService);
  }

  async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.administrators = await this.dbQueryService.getAccountAdminsByAccountId(this.accountId);
    this.recomputeVisibleAdministrators();
    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');
    this.activeStatuses = await this.dbQueryService.getAll(ActiveStatus);
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onRowSelected: this.onAccountAdminRowSelected,
    });
    AgFns.captureGridRouteParams(this.gridOptions, this.route, 'id');

    if (this.statusService.getWorkingStatus(this.account).isActiveReadOnly) {
      this.editLabel = 'Set Training';
      this.editLabelWidth = 100;
    }

    this.isValidateActivated = this.activationService.hasErrors;
    if (this.isValidateActivated) {
      this.activationService.activate(this.account.id);
    }

    this.setTitle('Account Administrators for ' + this.account.name);
    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const gridOptions = event.context.gridOptions;
    const [colDefs, sortModel] = this.getColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.applyGridRouteParams(gridOptions);
  }

  getColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      {
        ...AgFns.createButtonProps('', this.onEdit.bind(this), {
          calcLabel: this.calcEditLabel.bind(this),
          canDisplay: this.canEditAdmin.bind(this),
        }),
        ...AgFns.fixWidth(this.editLabelWidth),
        suppressHeaderMenuButton: true,
      },
      {
        headerName: '',
        ...AgFns.fixWidth(40),
        sortable: false,
        suppressHeaderMenuButton: true,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (params: any) => this.renderTrainingLockIcon(params.data as AccountAdmin),
      },
      { headerName: 'Name', field: 'proximityUser.fullName', filter: 'agTextColumnFilter', ...AgFns.flexWidth(150) },
      {
        headerName: 'Role',
        field: 'proximityUser.proximityRole.name',
        filter: 'agTextCOlumnFilter',
        ...AgFns.flexWidth(150),
      },
      { headerName: 'User Name', field: 'proximityUser.username', filter: 'agTextColumnFilter', ...AgFns.flexWidth(150) },
      {
        ...AgFns.createButtonProps('', this.onSendInvite.bind(this), { label: 'Send Invite', canDisplay: this.canEditAdmin.bind(this) }),
        ...AgFns.fixWidth(100),
        suppressHeaderMenuButton: true,
      },
      { headerName: 'Title', field: 'proximityUser.title', filter: 'agTextColumnFilter', ...AgFns.flexWidth(100) },

      { headerName: 'E-Mail', field: 'proximityUser.email', filter: 'agTextColumnFilter', ...AgFns.flexWidth(150) },
      // ProxAgFns.getWorkingStatusDef('Working Status', this.statusService),
      {
        ...ProxAgFns.getActiveStatusColDef('proximityUser.activeStatus.name'),
        ...AgFns.fixWidth(100),
        sortable: false,
      },
      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this), { canDisplay: this.isEditable.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'proximityUser.fullName', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  isEditable() {
    return this.account!.activeStatusId === ActiveStatusEnum.Hold;
  }

  private recomputeVisibleAdministrators() {
    this.visibleAdministrators = this.showInactive
      ? this.administrators
      : this.administrators.filter((a) => a.proximityUser?.activeStatusId !== ActiveStatusEnum.Inactive);
  }

  onShowInactiveChanged() {
    this.recomputeVisibleAdministrators();
    AgFns.refreshGrid(this.gridOptions, this.visibleAdministrators);
  }

  async onChangeActiveStatus(accountAdmin: AccountAdmin) {
    const valFn = async (selectedStatusId: ActiveStatusEnum, errs: string[]) => {
      if (!accountAdmin.entityAspect.validateEntity() || !accountAdmin.proximityUser.entityAspect.validateEntity()) {
        errs.push('Account Administrator failed validation test.  Fix Account Administrator before changing status.');
      }
      if (selectedStatusId != ActiveStatusEnum.Active) {
        if (this.account?.primaryAccountAdminId == accountAdmin.id) {
          errs.push(
            'Primary Account Administrator must be status: Active.  To change the status of this Account Administrator, select a different Primary Administrator for this Account, first.',
          );
        }
      }
      return true;
    };

    // TODO: PhaseOut logic not yet implemented — hide PhaseOut from status picker until it is.
    const isAvailableFn = (s) => {
      return s.id !== ActiveStatusEnum.Phaseout;
    };

    const activeStatusId = await this.statusService.onChangeActiveStatus(
      this,
      accountAdmin.proximityUser,
      'Account Administrator',
      valFn,
      isAvailableFn,
    );
    if (activeStatusId != null) {
      this.recomputeVisibleAdministrators();
      AgFns.refreshGrid(this.gridOptions, this.visibleAdministrators);
    }
    this.gridOptions.apiX?.refreshCells();
    if (activeStatusId != null && this.isValidateActivated) {
      if (!(await this.activationService.activate(this.account.id))) {
        this.account.activeStatusId = ActiveStatusEnum.Active;
        this.activationService.deactivate();
        this.isValidateActivated = false;
        await this.dbSaveService.saveSelectedChanges([this.account]);
        await this.dialogService.okDialog('Activation Status', "Account is successfully updated to 'Active'");
      }
    }
  }

  canEditAdmin(accountAdmin: AccountAdmin) {
    // const accountAdmin = row as AccountAdmin;
    return (
      this.authUser?.hasRight(this.ProximityRightEnum.CanManageAccountAdministration) || this.authUser?.accountAdminId == accountAdmin.id
    );
  }

  /**
   * Per-row label for the first column:
   *   Account not in read-only state -> "Edit"
   *   Account is read-only AND row is PAA -> "Set Training"
   *   Account is read-only AND row is non-Principal AccountAdmin -> "Read Only"
   */
  calcEditLabel(accountAdmin: AccountAdmin): string {
    const isReadOnly = this.statusService.getWorkingStatus(this.account).isActiveReadOnly;
    if (!isReadOnly) return 'Edit';
    return accountAdmin?.proximityUser?.proximityRole?.name === Roles.PrincipalAccountAdmin
      ? 'Set Training'
      : 'Read Only';
  }

  /** Training Mode column cell renderer. PAAs only; non-Principal admins show nothing. */
  private renderTrainingLockIcon(admin: AccountAdmin): string {
    if (admin?.proximityUser?.proximityRole?.name !== Roles.PrincipalAccountAdmin) return '';
    const until = admin.trainingUnlockedUntil;
    const isUnlocked = until != null && new Date(until).getTime() > Date.now();
    if (!isUnlocked) {
      return '<i class="fa-solid fa-lock text-amber-600" title="Locked for training"></i>';
    }
    const isPermanent = new Date(until!).getUTCFullYear() >= 9999;
    const tooltip = isPermanent ? 'Permanently unlocked' : `Unlocked until ${new Date(until!).toLocaleString()}`;
    return `<i class="fa-solid fa-lock-open text-green-700" title="${tooltip}"></i>`;
  }

  onEdit(row: AccountAdmin) {
    this.updateGridRouteParams(this.gridOptions, row.id);
    this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-administrators', row.id]);
  }

  // async onResetLogin(row: AccountAdmin) {
  //   await this.dbSaveService.postResetProximityUserLogin(row.proximityUser.id, Roles.PrincipalAccountAdmin);
  //   this.toastr.success('Account Administrator Login has been reset for ' + row.proximityUser.name);
  // }

  private async onSendInvite(row: AccountAdmin) {
    if (await this.dbSaveService.postSendUserInvite(row.proximityUser.id)) {
      this.toastr.success('Invite email was sent to ' + row.proximityUser.name);
    }
  }

  isReadOnly() {
    return (
      !this.hasRight(ProximityRightEnum.CanManageAccountAdministration) ||
      this.statusService.getWorkingStatus(this.account).isActiveReadOnly
    );
  }

  statusMessage() {
    if (!this.hasRight(ProximityRightEnum.CanManageAccountAdministration)) {
      return 'Not authorized';
    }

    let msg = '';
    if (this.account?.activeStatusId == ActiveStatusEnum.Active) {
      return 'Account is Active and Not Editable.  To edit, set Account status to Hold.';
    } else if (this.account?.activeStatusId == ActiveStatusEnum.Hold) {
      msg = 'Account is On-Hold and is Editable';
    } else {
      return 'Account is ' + this.account?.activeStatus.name + ' and is Not Editable';
    }
    return msg;
  }

  async onAdd() {
    if (this.isReadOnly()) {
      await this.dialogService.okDialog('Action Not Available', 'Account Administrators may not be added to inactive Account');
      return;
    }
    this.router.navigate(['account/accounts', this.accountId, 'manage', 'account-administrators', 'add']);
  }

  async onAccountAdminRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    this.onRowSelectedDefault(event);
    const aa = event.data as AccountAdmin;
    this.onAccountAdminChanged(aa);
  }

  async onAccountAdminChanged(aa: AccountAdmin) {
    if (aa == null) return;
    this.jobOrders = await this.dbQueryService.getJobOrdersByAccountAdminId(aa.id, this.includeHierarchy);
    this.jobOrders = _.sortBy(this.jobOrders, (jo) => {
      const status = jo.jobOrderStatusId == JobOrderStatusEnum.AwaitingApproval ? -100 : jo.jobOrderStatusId;
      return [status, jo.orderCreatedTs];
    });
    AgFns.refreshGrid(this.joGridOptions, this.jobOrders);
  }
}
